import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Tabs, Select } from "antd";

import LeadReport from "../components/dashboard/LeadReport";
import MarketingReport from "../components/dashboard/MarketingReport";
import ActivitiesReport from "../components/dashboard/ActivitiesReport";
import MyViewReport from "../components/dashboard/MyViewReport";
import RecommendedReport from "../components/dashboard/RecommendedReport";
import { AuthContext } from "../context/AuthContext";
import Loader from "../components/loader";
import { getAccountInfo } from "../actionCreator/commonActionCreator";
import { getToken } from "../utils/auth";
import { getPlatformReviews } from "../actionCreator/reputationManagerActionCreator";
import { searchUsers } from "../actionCreator/userManagementActionCreator";
import { getUserInfo } from "../utils/utils";
import { userRoles } from "../config";
import { getUserDisplayName } from "../utils/teamAccess";
import { getAccessibleDashboardUsers } from "../actionCreator/dashboardActionCreator";

const MyDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [permissions, setPermissions] = useState();
  const [filteredItems, setFilteredItems] = useState([]);
  const [activeTab, setActiveTab] = useState(
    searchParams.get("activeTab") || "1",
  );
  const { isAuthenticated, user } = useContext(AuthContext);
  const loading = useSelector((state) => state.CommonReducer.loading);
  const [selectedUser, setSelectedUser] = useState(user?.id);
  const [accessibleUsers, setAccessibleUsers] = useState([]);
  const [searchedUsers, setSearchedUsers] = useState([]);
  const { role } = getUserInfo();

  useEffect(() => {
    setSelectedUser(user?.id);
  }, [user]);

  useEffect(() => {
    setActiveTab(searchParams.get("activeTab") || "1");
  }, [searchParams]);

  useEffect(() => {
    const fetchUsers = async () => {
      if (!user?.id) return;

      const res = await dispatch(getAccessibleDashboardUsers());
      setAccessibleUsers(res || []);
    };
    fetchUsers();
  }, [dispatch, user]);

  useEffect(() => {
    if (!selectedUser || !accessibleUsers?.length) return;
    if (
      !accessibleUsers.some(
        (dashboardUser) => dashboardUser?.id === selectedUser,
      )
    ) {
      setSelectedUser(user?.id);
    }
  }, [accessibleUsers, selectedUser, user]);

  const dropdownUsers = useMemo(() => {
    const sourceUsers =
      role === userRoles.admin && searchedUsers.length
        ? [...accessibleUsers, ...searchedUsers]
        : accessibleUsers;

    return sourceUsers.filter(
      (value, index, array) =>
        array.findIndex((item) => item?.id === value?.id) === index,
    );
  }, [accessibleUsers, role, searchedUsers]);

  const canSwitchUsers =
    role === userRoles.admin ||
    accessibleUsers.filter((dashboardUser) => dashboardUser?.id).length > 1;

  const items = useMemo(
    () => [
      {
        key: "1",
        modules: ["Leads"],
        label: <span className="d-flex align-items-center gap-10">Leads</span>,
        children: <LeadReport userId={selectedUser} />,
      },
      {
        key: "2",
        modules: ["Leads"],
        label: (
          <span className="d-flex align-items-center gap-10">Marketing</span>
        ),
        children: <MarketingReport userId={selectedUser} />,
      },
      {
        key: "3",
        modules: ["Leads"],
        label: (
          <span className="d-flex align-items-center gap-10">Activities</span>
        ),
        children: <ActivitiesReport userId={selectedUser} />,
      },
      {
        key: "4",
        modules: ["Company", "Executive"],
        label: (
          <span className="d-flex align-items-center  gap-10">My View</span>
        ),
        children: <MyViewReport userId={selectedUser} />,
      },
      {
        key: "5",
        modules: ["Company", "Executive"],
        label: (
          <span className="d-flex align-items-center  gap-10">
            AI Recommendation
          </span>
        ),
        children: <RecommendedReport userId={selectedUser} />,
      },
    ],
    [selectedUser],
  );

  useEffect(() => {
    if (isAuthenticated && permissions && typeof permissions === "object") {
      const viewOnlyModules = Object.entries(permissions)
        .filter(([_, permissions]) =>
          permissions?.find((d) => d?.name === "View"),
        )
        .map(([moduleName]) => moduleName);
      setFilteredItems(
        items?.filter((d) =>
          d?.modules.some((item) => viewOnlyModules.includes(item)),
        ),
      );
    }
  }, [permissions, isAuthenticated, items]);

  useEffect(() => {
    const getData = async () => {
      if (isAuthenticated) {
        const res = await dispatch(getAccountInfo(getToken()));
        setPermissions(res?.permissions);
        dispatch(
          getPlatformReviews({
            page: 0,
            size: 3,
          }),
        );
      }
    };
    getData();
  }, [dispatch, isAuthenticated]);

  return (
    <div id="tabscompany">
      <h3 className="tw-text-text-primary tw-text-2xl tw-font-semibold tw-px-4 tw-mb-5">
        Welcome {user?.firstName} {user?.lastName},
      </h3>
      {loading ? (
        <Loader />
      ) : (
        <Tabs
          activeKey={
            searchParams.get("activeTab") ? activeTab : filteredItems[0]?.key
          }
          items={filteredItems}
          style={{ background: "transparent" }}
          tabBarExtraContent={
            canSwitchUsers && (
              <div className="tw-flex tw-items-center tw-gap-3 tw-mr-4">
                <span className="tw-text-sm tw-font-medium tw-text-sub-text">
                  View Dashboard For:
                </span>
                <Select
                  showSearch
                  placeholder="Select User"
                  value={selectedUser}
                  onSearch={(val) => {
                    if (role !== userRoles.admin) return;
                    if (!val?.trim()) {
                      setSearchedUsers([]);
                      return;
                    }
                    dispatch(searchUsers(val)).then((res) =>
                      setSearchedUsers(res?.content || []),
                    );
                  }}
                  onChange={(value) => {
                    if (value === selectedUser) return;
                    setSelectedUser(value);
                    if (role === userRoles.admin) {
                      setSearchedUsers([]);
                    }
                  }}
                  style={{ width: 220 }}
                  className="tw-custom-select"
                  optionFilterProp="label"
                  filterOption={role !== userRoles.admin}
                  options={[
                    { label: "My Dashboard", value: user?.id },
                    ...dropdownUsers?.map((u) => ({
                      label: getUserDisplayName(u),
                      value: u.id,
                    })),
                  ].filter(
                    (v, i, a) => a.findIndex((t) => t.value === v.value) === i,
                  )}
                />
              </div>
            )
          }
          onChange={(tab) => {
            setActiveTab(tab);
            searchParams.set("activeTab", tab);
            navigate(`${location.pathname}?${searchParams.toString()}`);
          }}
        />
      )}
    </div>
  );
};

export default MyDashboard;
